import React, { useState } from "react";
import { supabase } from "../lib/supabaseClient";
import { useTheme } from "../context/ThemeContext";
import { Sun, Moon } from "lucide-react";

export default function AuthScreen() {
  const { theme, toggleTheme } = useTheme();
  const [mode, setMode] = useState("signin");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState(null);

  const submit = async (e) => {
    e.preventDefault();
    setErr(null);
    setBusy(true);
    try {
      if (mode === "signin") {
        const { error } = await supabase.auth.signInWithPassword({ email, password });
        if (error) throw error;
      } else {
        const { error } = await supabase.auth.signUp({ email, password });
        if (error) throw error;
      }
    } catch (e) {
      setErr(e.message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="min-h-screen grid place-items-center bg-slate-50 dark:bg-slate-950 relative">
      {/* Theme Toggle Button */}
      <button
        onClick={toggleTheme}
        type="button"
        className="absolute top-4 right-4 p-2 rounded-lg bg-slate-100 dark:bg-white/10 hover:bg-slate-200 dark:hover:bg-white/15 border border-slate-300 dark:border-white/20 text-slate-700 dark:text-gray-100 transition-all duration-200"
        title={theme === 'dark' ? 'Switch to light mode' : 'Switch to dark mode'}
      >
        {theme === 'dark' ? <Sun className="w-4 h-4" /> : <Moon className="w-4 h-4" />}
      </button>
      
      <form
        onSubmit={submit}
        className="w-full max-w-sm rounded-2xl border border-slate-200 dark:border-white/10 bg-white dark:bg-slate-900 p-6 shadow-sm"
      >
        <h2 className="mb-4 text-xl font-semibold text-slate-900 dark:text-gray-100">
          {mode === "signin" ? "Sign in" : "Create account"}
        </h2>

        <label className="mb-2 block text-sm">
          <span className="mb-1 block text-xs text-slate-600 dark:text-gray-400">Email</span>
          <input
            type="email"
            className="w-full rounded-xl border border-slate-200 dark:border-white/10 bg-white dark:bg-slate-900 text-slate-900 dark:text-gray-100 px-3 py-2 text-sm"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            placeholder="you@domain.com"
            required
          />
        </label>

        <label className="mb-3 block text-sm">
          <span className="mb-1 block text-xs text-slate-600 dark:text-gray-400">Password</span>
          <input
            type="password"
            className="w-full rounded-xl border border-slate-200 dark:border-white/10 bg-white dark:bg-slate-900 text-slate-900 dark:text-gray-100 px-3 py-2 text-sm"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            placeholder="••••••••"
            required
          />
        </label>

        {err && <div className="mb-3 text-xs text-rose-600 dark:text-rose-400">{err}</div>}

        <button
          disabled={busy}
          className="w-full rounded-lg px-4 py-2 text-sm font-medium bg-slate-100 dark:bg-white/10 border border-slate-300 dark:border-white/20 text-slate-900 dark:text-gray-100 hover:bg-slate-200 dark:hover:bg-white/15 disabled:opacity-50"
        >
          {busy ? "Working…" : mode === "signin" ? "Sign in" : "Sign up"}
        </button>

        <div className="mt-3 text-xs text-center">
          {mode === "signin" ? (
            <>
              <span className="text-slate-600 dark:text-gray-400">No account?</span>{" "}
              <button
                type="button"
                className="rounded-lg px-2 py-1 text-xs bg-slate-100 dark:bg-white/10 border border-slate-300 dark:border-white/20 text-slate-900 dark:text-gray-100 hover:bg-slate-200 dark:hover:bg-white/15"
                onClick={() => setMode("signup")}
              >
                Sign up
              </button>
            </>
          ) : (
            <>
              <span className="text-slate-600 dark:text-gray-400">Have an account?</span>{" "}
              <button
                type="button"
                className="rounded-lg px-2 py-1 text-xs bg-slate-100 dark:bg-white/10 border border-slate-300 dark:border-white/20 text-slate-900 dark:text-gray-100 hover:bg-slate-200 dark:hover:bg-white/15"
                onClick={() => setMode("signin")}
              >
                Sign in
              </button>
            </>
          )}
        </div>
      </form>
    </div>
  );
}